import { useState } from "react";
import { useGetList } from "react-admin";
import { PageHeader } from "@/components/common/PageHeader";
import { Add } from "@mui/icons-material";
import { GhostButton } from "@/components/common/GhostButton";
import { DocumentRead, DocumentRecordType, DropzoneDocumentType, ResourceName, TargetSchema } from "@/types";
import { DocumentQueue } from "@/resources/documents/components/DocumentQueue";
import { DocumentDropzone } from "@/components/common/DocumentDropzone";
import { getResourceIcon } from "@/utils/icons";
import { PageContent } from "@/components/common/PageLayout";
import { SectionCard } from "@/components/common/SectionCard";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { featureFlags } from "@/utils/featureFlags";
import { PolicyCreateDialog } from "./components/PolicyCreateDialog";

export const PolicyCreate: React.FC = () => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const PolicyIcon = getResourceIcon(ResourceName.POLICIES);

  const { canCreate: canCreateDocuments, canList: canListDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);

  // Get policy-specific processing queue
  const {
    data: documents = [] as DocumentRead[],
    isLoading: documentsLoading,
    refetch: refetchDocuments,
  } = useGetList(ResourceName.DOCUMENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_datetime", order: "DESC" },
  });

  const handleUploadComplete = () => {
    // Refresh transformations to show new uploads
    void refetchDocuments();
  };

  return (
    <>
      <PageHeader
        customTitle="Create Policy"
        customSubtitle={
          featureFlags.policyIngestionEnabled
            ? "Upload your policy documents to get started. You can assign different insurance carriers to each file."
            : "Create and manage policies."
        }
        hideSectionTitle
        rightContent={
          <GhostButton
            label="Create Manually"
            startIcon={<Add />}
            onClick={() => {
              setOpenCreateDialog(true);
            }}
          />
        }
        headerIcon={<PolicyIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <PageContent>
        {/* Policy Upload Section — hidden when policy ingestion flag is off
            so users cannot trigger the v1 extraction pipeline through this
            page. The "Create Manually" CTA in the header remains the path
            forward in that state. */}
        {canCreateDocuments && featureFlags.policyIngestionEnabled && (
          <SectionCard>
            <DocumentDropzone
              documentType={DropzoneDocumentType.POLICIES}
              targetSchema={TargetSchema.POLICY}
              onUploadComplete={handleUploadComplete}
            />
          </SectionCard>
        )}

        {/* Processing Queue Section */}
        {canListDocuments && (
          <SectionCard title="Processing Queue" subtitle="View any processing policy uploads">
            <DocumentQueue
              documents={documents}
              isLoading={documentsLoading}
              onRefresh={refetchDocuments}
              recordType={DocumentRecordType.POLICIES}
            />
          </SectionCard>
        )}
      </PageContent>

      {/* Create Manual Dialog */}
      <PolicyCreateDialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} />
    </>
  );
};
